// ── [BEGIN temporary site password gate — remove when public launch happens] ─
// Private-testing shared-secret gate. Blocks the whole app until the user
// types the password (checked against /health with X-Site-Password). Once
// validated, the password is stored in localStorage so the api/client.ts
// wrapper can attach it to every subsequent API request.
//
// This is not real auth. It keeps strangers out; it will not stop anyone
// who can open devtools. See README TODO about Clerk migration.
// ────────────────────────────────────────────────────────────────────────
import { useEffect, useState, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'

import s from './SiteGate.module.css'

const SITE_PASSWORD_KEY = 'siteAccess'

/**
 * Routes that stay publicly accessible even with SITE_PASSWORD set.
 * Korean corporate law requires the company surface (splash +
 * marketing extension) to be publicly reachable; PIPA requires the
 * privacy policy to be public. App entry points (sign-in, sign-up,
 * teacher, student) stay gated. Mirrors TestingModeGate.tsx's
 * isPublicRoute (intentional duplication — both gates removed at
 * public launch and a shared helper would outlive its callers).
 */
function isPublicRoute(pathname: string): boolean {
  if (pathname === '/' || pathname === '/en') return true
  if (pathname === '/privacy' || pathname.startsWith('/privacy?')) return true
  return false
}

// Bilingual strings live inline because SiteGate renders above the i18n
// provider (it blocks the whole app, including route-based lang selection).
// New strings stay here too — adding them to i18n/en.ts + ko.ts would be
// both wrong (the gate renders above LangProvider) and pointless (these
// only ever appear in the pre-auth gate UI).
const COPY = {
  prompt: '비공개 테스트 — 비밀번호를 입력하세요 / Private testing — enter the password',
  placeholder: '비밀번호 / Password',
  submit: '입장 / Enter',
  checking: '확인 중… / Checking…',
  wrong: '비밀번호가 올바르지 않습니다 / Wrong password',
  // Transient failure (network blip / backend 5xx) — distinct from a
  // rejected password so the tester knows to retry rather than assuming
  // they mistyped the shared secret.
  transient:
    '연결할 수 없습니다. 다시 시도해 주세요 / Could not reach the server — please try again',
}

/**
 * Outcome of a ``/api/probe`` round-trip, narrowed into the three shapes
 * the two call sites care about. A bare boolean conflated "wrong password"
 * with "network blip", so a transient failure during the mount-time
 * re-check looked identical to a rejected password and wiped the cached
 * ``siteAccess`` key (audit #732).
 *
 * Mirrors the four-failure-shape posture hardened in
 * ``useTeacherClassData.loadClass`` (#103) and the sibling
 * ``TestingModeGate.recheckStoredPassword``: only an explicit rejection
 * (401/403 from the site_password_gate middleware) is treated as "wrong";
 * a ``fetch()`` rejection (DNS hiccup, offline window) or a 5xx/transient
 * status is ``transient`` so callers can preserve cached state.
 *
 *   ok        → 2xx (correct password; the middleware let the probe through)
 *   wrong     → 401/403 (the shared secret was rejected — genuinely wrong)
 *   transient → fetch() rejection OR any other non-2xx, non-401/403 status
 *               (5xx, or an unexpected 4xx like 400/404/429 from a proxy /
 *               rate-limiter / misroute) — server unreachable or hiccuping;
 *               the cached value should survive
 */
type ProbeOutcome = 'ok' | 'wrong' | 'transient'

async function verifyPassword(pw: string): Promise<ProbeOutcome> {
  let res: Response
  try {
    res = await fetch('/api/probe', {
      method: 'GET',
      headers: { 'X-Site-Password': pw },
    })
  } catch {
    // Network-level failure: the request never reached a server (DNS
    // failure, brief offline window, backend mid-reboot). This is NOT a
    // password rejection — surfacing it as ``wrong`` would wipe the
    // cached secret on a flaky connection. Same posture as
    // TestingModeGate.recheckStoredPassword.
    return 'transient'
  }
  // 2xx (204 = correct password) is the ONLY shape that means "the
  // middleware accepted the shared secret and let the probe through".
  // Default-to-``transient`` (below) rather than default-to-``ok`` so an
  // unexpected status can never masquerade as a verified password
  // (CodeRabbit on PR #773).
  if (res.ok) return 'ok'
  // 401/403 = the site_password_gate middleware rejected the shared
  // secret. Only these mean "wrong password".
  if (res.status === 401 || res.status === 403) return 'wrong'
  // Everything else — 5xx (backend wobble) AND any other 4xx we did not
  // enumerate (e.g. 400/404 from a proxy misroute, 429 from a
  // rate-limiter) — is treated as ``transient``: the server is reachable
  // but not giving us a clean accept/reject, so we must NOT interpret it
  // as a verified password. Preserving the cached value and surfacing the
  // retry path is the safe failure mode. Log the unexpected status so a
  // probe-contract drift is debuggable (CodeRabbit on PR #773).
  if (res.status < 500) {
    console.warn(`SiteGate: unexpected /api/probe status ${res.status}`)
  }
  return 'transient'
}

export default function SiteGate({ children }: { children: ReactNode }) {
  const location = useLocation()
  const [unlocked, setUnlocked] = useState<boolean>(
    () => !!localStorage.getItem(SITE_PASSWORD_KEY),
  )
  const [value, setValue] = useState('')
  // ``null`` = no error. ``wrong`` = the secret was rejected (401/403).
  // ``transient`` = the server was unreachable / hiccuping — a distinct
  // message so the tester retries instead of assuming they mistyped
  // (audit #732).
  const [error, setError] = useState<null | 'wrong' | 'transient'>(null)
  const [checking, setChecking] = useState(false)

  useEffect(() => {
    if (!unlocked || checking) return
    const stored = localStorage.getItem(SITE_PASSWORD_KEY)
    if (!stored) return
    // Re-validate a stored password on mount in case it was revoked.
    //
    // Only an explicit ``wrong`` (401/403 — the secret was rotated /
    // revoked server-side) clears the cache. A ``transient`` outcome
    // (network blip, backend reboot, 5xx) must PRESERVE the cached value:
    // a defensive wipe-on-any-error would log the tester out on a flaky
    // connection and — because SiteGate and TestingModeGate share the
    // ``siteAccess`` key and both run this re-check on the same load —
    // leave the two gates in an inconsistent state (audit #732). Mirrors
    // TestingModeGate.recheckStoredPassword's trust-the-cache posture;
    // the next real API call surfaces any genuine auth issue.
    void verifyPassword(stored).then((outcome) => {
      if (outcome === 'wrong') {
        // Guard against a stale result clobbering a fresh re-validation.
        // ``verifyPassword(stored)`` is async; while it was in flight
        // another tab (or the sibling TestingModeGate, which shares this
        // key) may have re-validated and rewritten ``siteAccess`` with a
        // newer secret. A late ``'wrong'`` for the OLD value must not wipe
        // that newer value and relock the tab — only clear the cache if
        // what's stored now is still the exact value we probed (CodeRabbit
        // on PR #773).
        if (localStorage.getItem(SITE_PASSWORD_KEY) !== stored) return
        localStorage.removeItem(SITE_PASSWORD_KEY)
        setUnlocked(false)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Listen for in-tab unlock signals from sibling gates / the splash's
  // embedded ``<TestingPasswordPanel>``. Without this, entering the
  // password via SplashScreen wouldn't propagate here — ``unlocked`` was
  // initialized at mount and would otherwise stay false until a remount,
  // re-prompting the user on the next gated route. Same family as #247
  // / PR #248. Closes #249.
  //
  // Trust note: the dispatchers fire only after a successful
  // ``/api/probe`` round-trip, so the listener can flip state without
  // re-validating. The mount-time effect above still covers the
  // returning-visitor case where localStorage held a stale-and-revoked
  // password.
  useEffect(() => {
    const onUnlock = () => setUnlocked(true)
    window.addEventListener('sitegate:unlocked', onUnlock)
    return () => window.removeEventListener('sitegate:unlocked', onUnlock)
  }, [])

  // Cross-tab sync via the native ``storage`` event (#253A). Fires only
  // in OTHER tabs when this origin's localStorage changes — never in
  // the originating tab. So this listener handles "user just unlocked
  // / locked the gate in tab B" → mirror to tab A's UI immediately,
  // without forcing a refresh.
  //
  // Sync directly from ``e.newValue`` (truthy = unlocked, null =
  // locked) rather than reading ``unlocked`` from the closure. CodeRabbit
  // on PR #271 caught the original shape — ``unlocked`` in deps caused
  // the listener to re-bind on every state flip, opening a brief
  // unbind→bind window where rapid consecutive storage events from
  // another tab could land outside the listener and silently drop. The
  // setState-with-same-value bail-out makes the no-conditional path
  // safe (React skips identical-primitive re-renders).
  //
  // Trust note: another tab's ``setItem`` only happens after a
  // successful ``/api/probe`` round-trip there, so flipping locally
  // without re-validating is safe (same trust shape as the in-tab
  // event above). The mount-time effect still covers the
  // returning-visitor case where localStorage held a stale-and-revoked
  // password.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== SITE_PASSWORD_KEY) return
      setUnlocked(Boolean(e.newValue))
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  // Public routes always pass through, even when the gate would
  // otherwise be locked. The splash itself replaces its CTAs with
  // the testing notice for un-bypassed visitors (see SplashScreen).
  if (isPublicRoute(location.pathname)) return <>{children}</>

  if (unlocked) return <>{children}</>

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!value || checking) return
    setChecking(true)
    setError(null)
    const outcome = await verifyPassword(value)
    setChecking(false)
    if (outcome === 'ok') {
      localStorage.setItem(SITE_PASSWORD_KEY, value)
      // Notify in-tab listeners that the site-password gate just
      // cleared. Native ``storage`` events only fire cross-tab, so
      // components mounted alongside the gate (e.g. ProviderSwitcher,
      // gh#247) need a same-tab signal to know it's safe to start
      // making authed requests. Removed alongside the rest of the
      // site-password machinery at public launch.
      window.dispatchEvent(new Event('sitegate:unlocked'))
      setUnlocked(true)
    } else if (outcome === 'wrong') {
      // Genuine rejection — clear the field so the tester re-types.
      setError('wrong')
      setValue('')
    } else {
      // ``transient`` — the server was unreachable / hiccuping. Do NOT
      // store the (possibly-correct) password as validated, and do NOT
      // mislabel this as a wrong password. Keep what they typed so a
      // one-click retry doesn't force a re-type on a flaky connection.
      setError('transient')
    }
  }

  return (
    <div className={s.gate}>
      <form className={s.card} onSubmit={handleSubmit}>
        <h1 className={s.title}>eehae</h1>
        <p className={s.subtitle}>{COPY.prompt}</p>
        <input
          type="password"
          className={[s.input, error && s.inputError].filter(Boolean).join(' ')}
          value={value}
          // Surface the wrong/transient state to assistive tech: mark the
          // field invalid and point screen readers at the error node so
          // the failure reason is announced and associated with the input
          // (CodeRabbit on PR #773).
          aria-invalid={Boolean(error)}
          aria-describedby={error ? 'sitegate-error' : undefined}
          onChange={(e) => {
            setValue(e.target.value)
            if (error) setError(null)
          }}
          placeholder={COPY.placeholder}
          autoFocus
          disabled={checking}
        />
        {error && (
          <div id="sitegate-error" role="alert" className={s.error}>
            {error === 'wrong' ? COPY.wrong : COPY.transient}
          </div>
        )}
        <button
          type="submit"
          className={s.button}
          disabled={!value || checking}
        >
          {checking ? COPY.checking : COPY.submit}
        </button>
      </form>
    </div>
  )
}
